import React from "react";
import { Part, PartWithRepartition } from "./types";
import { FORTUNE_WHEEL_CONSTANTS, PARTS_COLORS } from "./constants";
import { getRandomElement } from "./functions";

const {
  wheelRadius,
  wheelInnerLineWidth,
  wheelInnerLineColor,
  wheelTurnsPerSecond,
  wheelFirstLinearDuration,
  wheelFirstEaseOutDuration,
  wheelSecondLinearDuration,
  wheelSecondEaseOutDuration,
} = FORTUNE_WHEEL_CONSTANTS;

export const withRepartition = (parts: Part[]): PartWithRepartition[] => {
  const totalWeight = parts.reduce((sum, part) => sum + part.weight, 0);
  let startAngle = 0;

  return parts.map((part) => {
    const angle = (part.weight / totalWeight) * 2 * Math.PI;
    const endAngle = startAngle + angle;
    const partWithRepartition = { ...part, startAngle, endAngle };
    startAngle = endAngle;
    return partWithRepartition;
  });
};

export const createWheel = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  parts: PartWithRepartition[]
) => {
  const canvas = canvasRef.current;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const center = { x: wheelRadius, y: wheelRadius };

  parts.forEach((part) => {
    ctx.beginPath();
    ctx.moveTo(center.x, center.y);
    ctx.arc(center.x, center.y, wheelRadius, part.startAngle, part.endAngle);
    ctx.closePath();
    ctx.fillStyle = part.color;
    ctx.fill();
    ctx.lineWidth = wheelInnerLineWidth;
    ctx.strokeStyle = wheelInnerLineColor;
    ctx.stroke();
  });

  // Center hub — clean flat circle
  ctx.beginPath();
  ctx.arc(center.x, center.y, wheelRadius / 7, 0, 2 * Math.PI);
  ctx.closePath();
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = wheelInnerLineColor;
  ctx.stroke();
};

export const spinWheel = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  spinRef: React.MutableRefObject<number>
) => {
  console.log("spinning");
  const canvas = canvasRef.current;
  if (!canvas) return;
  spinRef.current += 2 * Math.PI;
  canvas.style.transition = `transform ${100 / wheelTurnsPerSecond}s linear`;
  canvas.style.transform = `rotate(${spinRef.current}rad)`;
};

export const stopWheel = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  spinRef: React.MutableRefObject<number>,
  partsWithRepartition: PartWithRepartition[],
  setWinner: React.Dispatch<React.SetStateAction<Part | undefined>>
) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  // const randomDuration = Math.random() * 1.3 + 1;
  const randomDuration = Math.random() * 1.3 + 1;
  const randomWheelFirstLinearDuration =
    wheelFirstLinearDuration * randomDuration;
  const randomWheelFirstEaseOutDuration =
    wheelFirstEaseOutDuration * randomDuration;
  const randomWheelSecondLinearDuration =
    wheelSecondLinearDuration * randomDuration;
  const randomWheelSecondEaseOutDuration =
    wheelSecondEaseOutDuration * randomDuration;
  const applyLinearSpin = () => {
    console.log("linear spinning");
    spinRef.current +=
      2 * Math.PI * wheelTurnsPerSecond * randomWheelFirstLinearDuration;
    canvas.style.transform = `rotate(${spinRef.current}rad)`;
    canvas.style.transition = `transform ${randomWheelFirstLinearDuration}s linear`;
  };
  const applyEaseOutSpin = () => {
    console.log("ease-out spinning");
    spinRef.current +=
      2 * Math.PI * (wheelTurnsPerSecond / 2) * randomWheelFirstEaseOutDuration;
    canvas.style.transform = `rotate(${spinRef.current}rad)`;
    canvas.style.transition = `transform ${randomWheelFirstEaseOutDuration}s ease-out`;
  };
  const applySecondLinearSpin = () => {
    console.log("second linear spinning");
    spinRef.current +=
      2 * Math.PI * (wheelTurnsPerSecond / 4) * randomWheelSecondLinearDuration;
    canvas.style.transform = `rotate(${spinRef.current}rad)`;
    canvas.style.transition = `transform ${randomWheelSecondLinearDuration}s linear`;
  };
  const applySecondEaseOutSpin = () => {
    console.log("second ease-out spinning");
    spinRef.current +=
      2 * Math.PI * (wheelTurnsPerSecond / 6) * randomWheelSecondEaseOutDuration;
    canvas.style.transform = `rotate(${spinRef.current}rad)`;
    canvas.style.transition = `transform ${randomWheelSecondEaseOutDuration}s ease-out`;
  };

  const getWinner = () => {
    const finalAngle =
      ((-spinRef.current - 0.5 * Math.PI) % (2 * Math.PI)) + 2 * Math.PI;
    const winner = partsWithRepartition.find((part) => {
      return part.startAngle <= finalAngle && finalAngle <= part.endAngle;
    });
    setWinner(winner);
    if (winner) {
      spinRef.current +=
        finalAngle - (winner?.endAngle + winner?.startAngle) / 2;
    }
    canvas.style.transform = `rotate(${spinRef.current}rad)`;
  };

  applyLinearSpin();
  setTimeout(() => {
    applyEaseOutSpin();
    setTimeout(() => {
      applySecondLinearSpin();
      setTimeout(() => {
        applySecondEaseOutSpin();
        setTimeout(getWinner, randomWheelSecondEaseOutDuration * 1000);
      }, randomWheelSecondLinearDuration * 1000);
    }, (randomWheelFirstEaseOutDuration - 1) * 1000);
  }, randomWheelFirstLinearDuration * 1000);
};

export const pickColor = (parts: Part[]): string => {
  const takenColors = new Set(parts.map((part) => part.color));
  if (takenColors.size === PARTS_COLORS.length) {
    return getRandomElement(PARTS_COLORS) as string;
  }
  const availableColors = PARTS_COLORS.filter(
    (color) => !takenColors.has(color)
  );
  return getRandomElement(availableColors) as string;
};
